import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { GraduationCap, Plus, Loader2, CheckCircle2, GripVertical } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { listTrainingCatalog, upsertCourse, reorderCourses } from "@/lib/training.functions";
import { getCourseCover } from "@/lib/course-covers";
import { useAuth } from "@/lib/auth";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const Route = createFileRoute("/_authenticated/training/")({
  component: TrainingCatalogPage,
});

function TrainingCatalogPage() {
  const { isPlatformAdmin } = useAuth();
  const router = useRouter();
  const fetchCatalog = useServerFn(listTrainingCatalog);
  const createCourseFn = useServerFn(upsertCourse);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["training-catalog"],
    queryFn: () => fetchCatalog(),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newLibraryId, setNewLibraryId] = useState<string>("");

  const createMut = useMutation({
    mutationFn: async () => createCourseFn({ data: {
      library_id: newLibraryId,
      title: newTitle.trim(),
      description: newDescription.trim() || null,
      is_published: true,
    }}),
    onSuccess: ({ course }) => {
      toast.success("Course created");
      setCreateOpen(false);
      setNewTitle(""); setNewDescription("");
      refetch();
      router.navigate({ to: "/training/courses/$courseId", params: { courseId: course.id } });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to create course"),
  });

  if (isLoading) {
    return <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading library…</div>;
  }

  const libraries = data?.libraries ?? [];
  const courses = data?.courses ?? [];
  const progressByCourse = data?.progressByCourse ?? {};

  return (
    <div className="p-6 sm:px-10 lg:px-16 max-w-6xl mx-auto space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary/10 p-2 text-primary"><GraduationCap className="h-6 w-6" /></div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Training Library</h1>
            <p className="text-sm text-muted-foreground">Foundational courses and your account's training content.</p>
          </div>
        </div>
      </header>

      {libraries.map((lib: any) => {
        const libCourses = courses.filter((c: any) => c.library_id === lib.id);
        const canCreate = lib.library_type === "foundation" ? isPlatformAdmin : true;
        const canReorder = canCreate;
        return (
          <section key={lib.id} className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{lib.name}</h2>
                {lib.description && <p className="text-sm text-muted-foreground">{lib.description}</p>}
              </div>
              {canCreate && (
                <Button size="sm" variant="outline" onClick={() => { setNewLibraryId(lib.id); setCreateOpen(true); }}>
                  <Plus className="h-4 w-4 mr-1" /> New course
                </Button>
              )}
            </div>

            {libCourses.length === 0 ? (
              <EmptyState
                title="No courses in this library yet"
                description={canCreate ? "Add the first course to this library." : "Check back soon, new lessons are added here."}
              />
            ) : (
              <CourseGrid
                libraryId={lib.id}
                courses={libCourses}
                progressByCourse={progressByCourse}
                canReorder={canReorder}
                onReordered={refetch}
              />
            )}
          </section>
        );
      })}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New course</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Library</Label>
              <Select value={newLibraryId} onValueChange={setNewLibraryId}>
                <SelectTrigger><SelectValue placeholder="Pick a library" /></SelectTrigger>
                <SelectContent>
                  {libraries.map((l: any) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Course title" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="Optional summary" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createMut.mutate()}
              disabled={!newTitle.trim() || !newLibraryId || createMut.isPending}
            >
              {createMut.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CourseGrid({
  libraryId,
  courses,
  progressByCourse,
  canReorder,
  onReordered,
}: {
  libraryId: string;
  courses: any[];
  progressByCourse: Record<string, { total: number; completed: number }>;
  canReorder: boolean;
  onReordered: () => void;
}) {
  const reorderFn = useServerFn(reorderCourses);
  const [items, setItems] = useState<any[]>(courses);
  useEffect(() => { setItems(courses); }, [courses]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ids = useMemo(() => items.map((c) => c.id), [items]);

  const reorderMut = useMutation({
    mutationFn: async (ordered_ids: string[]) => reorderFn({ data: { library_id: libraryId, ordered_ids } }),
    onSuccess: () => onReordered(),
    onError: (e: any) => {
      toast.error(e?.message ?? "Failed to reorder");
      setItems(courses);
    },
  });

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((c) => c.id === active.id);
    const newIdx = items.findIndex((c) => c.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    reorderMut.mutate(next.map((c) => c.id));
  };

  if (!canReorder) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => (
          <CourseCard key={c.id} course={c} progress={progressByCourse[c.id]} />
        ))}
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <SortableCourseCard key={c.id} course={c} progress={progressByCourse[c.id]} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableCourseCard({ course, progress }: { course: any; progress?: { total: number; completed: number } }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: course.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute top-2 right-2 z-10 rounded-md bg-background/80 backdrop-blur p-1.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing border shadow-sm"
        aria-label="Drag to reorder"
        onClick={(e) => e.preventDefault()}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </button>
      <CourseCard course={course} progress={progress} />
    </div>
  );
}

function CourseCard({ course: c, progress }: { course: any; progress?: { total: number; completed: number } }) {
  const p = progress ?? { total: 0, completed: 0 };
  const pct = p.total > 0 ? Math.round((p.completed / p.total) * 100) : 0;
  return (
    <Link to="/training/courses/$courseId" params={{ courseId: c.id }} className="block group/card">
      <Card className="h-full overflow-hidden transition-shadow group-hover/card:shadow-md">
        <div className="aspect-[4/3] bg-muted overflow-hidden relative">
          <img
            src={getCourseCover(c.title, c.cover_image_url)}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover/card:scale-[1.03] [filter:hue-rotate(110deg)] dark:[filter:none]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card/80 via-card/10 to-transparent" />
        </div>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base leading-tight">{c.title}</CardTitle>
            {!c.is_published && <Badge variant="outline" className="shrink-0">Draft</Badge>}
          </div>
          {c.description && <p className="text-xs text-muted-foreground line-clamp-2">{c.description}</p>}
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{p.completed} / {p.total} lessons</span>
            {pct === 100 && p.total > 0 && (
              <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3 w-3" /> Complete</span>
            )}
          </div>
          <Progress value={pct} className="h-1.5" />
        </CardContent>
      </Card>
    </Link>
  );
}
